import { formatDate, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID, ɵDEFAULT_LOCALE_ID } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, empty, filter, firstValueFrom, of, shareReplay, skip, switchMap, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, map, takeUntil, throttleTime } from 'rxjs/operators';
import { environment } from '../_environments/environment';
import { Box, BoxPeriod } from '../_model/box';
import { CouponcodeCode } from '../_model/couponcode';
import { CustomerLifestyle } from '../_model/customer';
import { Currency, Flowtype, Order, Orderline, Orderstatus, OrderWithTaxDetails, Paymentmethod, Shippingmethod } from '../_model/order';
import { CustomerCreateOrderLine } from '../_model/pricing';
import { Product } from '../_model/product';
import { Recipe } from '../_model/recipe';
import { Entitytype } from '../_model/redirect';
import { Countrycode, Sortdirection, TableParameters, TableResponse } from '../_model/shared';
import { Originentitytype } from '../_model/subscription';
import { MyValidators } from '../_validators/MyValidators';
import { AddressService } from './AddressService';
import { AuthService } from './AuthService';
import { BoxService } from './BoxService';
import { CustomerService } from './CustomerService';
import { GlobalService } from './GlobalService';
import { PricingService } from './PricingService';
import { ProductService } from './ProductService';
import { RecipeService } from './RecipeService';
import { SessionstorageService } from './SessionstorageService';
import { WINDOW } from './WindowService';
import { Role } from 'src/_model/user';
import { ToastService } from './Toast.service';
import { Snackbarerrorformat } from 'src/_theme/snackbarformats';
import { CouponcodeService } from './CouponcodeService';

const MAX_DELIVERY_INSTRUCTIONS_LENGTH = 500;

@Injectable({ providedIn: 'root' })
export class OrderService {

  rnd: any;

  singleOrderForm: FormGroup
  subscriptionOrderflowLines = new FormArray([]);

  telemarketingForm: FormGroup;

  //will be updated by the selected deliverydate to be as accurate as possible
  assumedDeliverydate = formatDate(this._globalService.addDays(new Date(), 8), 'yyyy-MM-dd', ɵDEFAULT_LOCALE_ID);

  //is customercreateorderRequest
  orderFlowForm: FormGroup;

  recipeAvailabilityPeriod$ = new BehaviorSubject<{ from: string; to: string } | null>(null);
  maxAvailableDeliveryDate: string | null = null;

  orderlistRefreshTime: number = environment.dataRefreshTime;
  orderlistRefreshTimer: any;

  shownPage: number = 0;
  totalPages: number = 1;
  pagesize: number = 5;
  totalOrders: number = 0;
  private _orders: Order[] = [];
  public OrderlistSubject: BehaviorSubject<Order[]> = new BehaviorSubject<Order[]>(null);
  public Orderlist: Subject<{ page: number, pagesize: number }> = new Subject();
  public singleorderRecipeCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  private _refreshOrderflowForm$ = new Subject<void>();
  constructor(
    private _http: HttpClient,
    public fb: FormBuilder,
    private _myValidators: MyValidators,
    private _recipeService: RecipeService,
    private _boxService: BoxService,
    private _authService: AuthService,
    private _productService: ProductService,
    private _pricingService: PricingService,
    private _globalService: GlobalService,
    private _customerService: CustomerService,
    private _addressService: AddressService,
    private _translateService: TranslateService,
    private _sessionStorage: SessionstorageService,
    private _couponcodeService: CouponcodeService,
    private _toastService: ToastService,
    @Inject(WINDOW) private window: Window,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {

    this.rnd = Math.random();

    let customeraddressForm = this._customerService.initiateCustomerAddress();
    customeraddressForm.setControl('address', this._addressService.addressForm)
    customeraddressForm.get('addressid').removeValidators(Validators.required);

    this.initOrderflowForm();

    this.singleOrderForm = this.initSingleOrderform();
    this.telemarketingForm = new FormGroup({
      steps: this.fb.array([
        // STEP-0
        new FormGroup({
          calculatedPrice: new FormControl(null),
          shipping: new FormControl(0),
          shippingmethod: new FormControl(Shippingmethod.DOOR_DELIVERY),
          deliverydate: new FormControl(''),
          recipecard: new FormControl(false),
          orderlines: this.fb.array([], Validators.required),
          shippingaddress: new FormControl(null)
        }),
        //STEP-1
        new FormGroup({
          customer: this._customerService.customerForm,
          address: this._addressService.shippingAddressForm,
          user: this._customerService.userForm
        }),
        //newsletteroptin: new FormControl(false),
        //STEP-2
        customeraddressForm,
        //STEP-3
        new FormGroup({
          startdate: new FormControl(null, Validators.required),
          storepickup: new FormControl(false),
          store: new FormControl(''),
          subscriptionid: new FormControl(null),
          deliverytimeframe: new FormControl(null, Validators.required),
          storedata: new FormControl(''),
          deliveryinstructions: new FormControl('', Validators.maxLength(500)),
        }),
        //STEP-4
        new FormGroup({
          newsletteroptin: new FormControl(false),
          //termsandconditions: new FormControl(false, Validators.requiredTrue),
          couponcode: new FormControl(null),
          paymentmethod: new FormControl(null, Validators.required),
          creditcardnetwork: new FormControl(null, Validators.required)
        })
      ])
    });

    this.Orderlist.pipe(
      shareReplay(),
      filter(() => !!this._authService.user()),
      filter(() => this.shownPage <= this.totalPages),
      switchMap((params: any) => {
        // Initialize shownPage from params to make sure it is initialized
        if (params && params.page !== null) {
          this.shownPage = params.page;
        }
        let tblparams = new TableParameters(params && params.page !== null ? params.page : this.shownPage, params && params.pagesize ? params.pagesize : this.pagesize, 'orderid', Sortdirection.DESC)

        return this.getOrdersTable(this._authService.user().customerid, tblparams as TableParameters);
      })
    ).subscribe(
      result => {
        this.OrderlistSubject.next(this._orders);

        if (!result) { return; }
        this.OrderlistSubject.next(this._orders);
        this.resetTimer();
        return result;
      }, error => {
        this.OrderlistSubject.error(error);
        return error;
      })

    this._authService.isAuthenticated$.pipe(
      distinctUntilChanged()
    ).subscribe(
      value => {
        if (!value) {
          this.clearNewOrderForms();
          this.initOrderflowForm();
          this.clearCustomerOrderdata()
        } else {
          this.Orderlist.next(null);
        }
      },
      () => {
        this.clearNewOrderForms();
        this.clearCustomerOrderdata()
      },
      () => {
        this.clearNewOrderForms();
        this.clearCustomerOrderdata()
      });

    this._translateService.onLangChange.pipe(skip(1)).subscribe(res => {
      //removes object from lines to let it retrieve the new translated objects to show the correct translated titles
      this.orderlines.controls.forEach(ctrl => {
        ctrl.get('object').reset();
      })
      //sets the sessionstorage because the valuechanges check takes too long and doesnt write it to the sessionstorage it before the reload
      this.saveNewOrderInSession()
    })

    this.singleOrderForm.valueChanges.pipe(
      throttleTime(500, null, { leading: false, trailing: true })
    ).subscribe(() => {
      this.saveNewOrderInSession();
    });

    this.order.get('recipecard').valueChanges.subscribe(() => {
      this.setCalculatedPrices(Flowtype.SINGLEORDER)
    })
    this.TMorder.get('recipecard').valueChanges.subscribe(() => {
      this.setCalculatedPrices(Flowtype.TELEMARKETING)
    })

    this.deliveryinfo.get([0]).get('startdate').valueChanges.subscribe(date => {
      if (!date) return;
      this.assumedDeliverydate = date;
    })

    //Subscriptionorderflow orderlines
    this.subscriptionOrderflowLines.valueChanges.subscribe({
      next: res => {
        this._sessionStorage.setObject('subscriptionorderflowlines', res)
      }
    })
    const SessionSubscriptionOrderFlowlines = _sessionStorage.getObject('subscriptionorderflowlines') as CustomerCreateOrderLine[];
    if (SessionSubscriptionOrderFlowlines) {
      SessionSubscriptionOrderFlowlines.forEach(l => {
        this.addOrderline(l, false, Flowtype.SUBSCRIPTION);
      })
    }

    const session = _sessionStorage.getObject('neworder');
    if (session) {
      //customer shizzl will be managed by the customerservice if the customer already exists
      const customerSession = _sessionStorage.getObject('customer')
      if (customerSession && !customerSession?.id) {

        customerSession.customerAddresses.forEach(rec => {
          this._customerService.addCustomerAddress(rec);
        });
        customerSession.customerLifestyles.forEach(rec => {
          this._customerService.addLifeStyle(rec);
        });
        customerSession.customerAllergens.forEach(rec => {
          this._customerService.addAllergen(rec);
        });
        this._customerService.customerForm.patchValue(customerSession)
        if (this._customerService.customerForm.get('address'))
          this._customerService.customerForm.get('address').patchValue(customerSession.address)
      }
      delete session.steps[1].user
      delete session.steps[1].customer
      delete session.steps[1].address

      session.steps[0].order[0].orderlines.forEach(line => {
        this.addOrderline(line);
      })
      this.singleOrderForm.patchValue(session, { emitEvent: false });

      //updates assumeddeliverydate based on the selected startdate
      if (this.deliveryinfo.get([0]).get('startdate').value) {
        const date = this.deliveryinfo.get([0]).get('startdate').value
        //checks if the date is bigger then today
        if (new Date(date).getTime() < new Date().getTime()) {
          this.deliveryinfo.get([0]).get('startdate').setValue(null);
          return;
        }
        this.assumedDeliverydate = this.deliveryinfo.get([0]).get('startdate').value
      }

      this.setNewOrderCouponcode(this._couponcodeService.couponcode$.value); //makes sure that the couponcode is not overwritten by the calculateprice request
      this.setCalculatedPrices();
    } else {
      this.clearNewOrderForms();
    }

    this._couponcodeService.couponcode$.subscribe(couponcode => {
      this.setNewOrderCouponcode(couponcode)
    })
  }
  get getNewOrderForm(): FormGroup {
    //fills in the basevalues when is empty form
    if (!this.singleOrderForm.controls.steps.get([0]).get('order').get([1]).get('countrycode').value) {
      this.singleOrderForm.controls.steps.get([0]).get('order').get([0]).get('shippingmethod').setValue(Shippingmethod.DOOR_DELIVERY);
      this.singleOrderForm.controls.steps.get([0]).get('order').get([1]).get('countrycode').setValue(this._globalService.getPricecountry() || Countrycode.NL);
      this.singleOrderForm.controls.steps.get([1]).get('customer').get('brandid').setValue(environment.brandid);
      if (this.singleOrderForm.controls.steps.get([1]).get('user'))
        this.singleOrderForm.controls.steps.get([1]).get('user').get('brandid').setValue(environment.brandid);
      this.singleOrderForm.controls.steps.get([0]).get('order').get([2]).get('deliveryinfo').get([0]).get('storepickup').setValue(false);
      //just to be sure, remove it
      this._customerService.customerForm.get('email').removeAsyncValidators(this._myValidators.Email);
    }
    return this.singleOrderForm;
  }
  get getNewTMOrderForm(): FormGroup {
    //fills in the basevalues when is empty form
    if (!this.telemarketingForm.controls.steps.get([2]).get('address').get('countrycode').value) {
      this.telemarketingForm.controls.steps.get([0]).get('shippingmethod').setValue(Shippingmethod.DOOR_DELIVERY);
      this.telemarketingForm.controls.steps.get([2]).get('address').get('countrycode').setValue(this._globalService.getPricecountry() || Countrycode.NL);
      // this.telemarketingForm.controls.steps.get([1]).get('brandid').setValue(environment.brandid);
      /*      this.telemarketingForm.controls.steps.get([1]).get('user').get('brandid').setValue(environment.brandid);*/
      this.telemarketingForm.controls.steps.get([3]).get('storepickup').setValue(false);
      this._customerService.customerForm.get('email').addAsyncValidators(this._myValidators.Email);
    }
    return this.telemarketingForm;
  }

  private get steps(): FormArray {
    return this.getNewOrderForm.controls.steps as FormArray;
  }
  get deliveryinfo(): FormArray {
    return this.steps.controls[0].get('order').get([2]).get('deliveryinfo') as FormArray;
  }
  get customer(): FormGroup {
    return this.steps.controls[1].get('customer') as FormGroup;
  }
  get address(): FormGroup {
    return this.customer.get('address') as FormGroup;
  }
  get customerAddresses(): FormArray {
    return this.customer.get('customerAddresses') as FormArray;
  }
  get customerLifestyles(): FormArray {
    return this.customer.get('customerLifestyles') as FormArray;
  }
  get customerAllergens(): FormArray {
    return this.customer.get('customerAllergens') as FormArray;
  }
  get countrycode(): FormControl {
    return this.steps.controls[0].get('order').get([1]).get('countrycode') as FormControl
  }
  get postalcode(): FormControl {
    return this.steps.controls[0].get('order').get([1]).get('postalcode') as FormControl
  }
  get userRoles(): FormArray {
    return this.steps.controls[1].get('user.userroles') as FormArray;
  }
  get order(): FormGroup {
    return this.steps.controls[0].get('order').get([0]) as FormGroup;
  }
  get orderlines(): FormArray {
    return this.order.get('orderlines') as FormArray;
  }
  get orderrequest(): FormGroup {

    //const dateToday = formatDate(new Date(), 'yyyy-MM-dd', ɵDEFAULT_LOCALE_ID);
    const dateNextWeek = formatDate(new Date(new Date().getTime() + 8 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd', ɵDEFAULT_LOCALE_ID);

    const order = new FormGroup({
      customerid: new FormControl(this.customer.get('id').value),
      email: new FormControl(this.customer.get('email').value),
      brandid: new FormControl(environment.brandid, [Validators.required, Validators.min(1)]),
      originentitytype: new FormControl(null),
      recipecard: new FormControl(this.order.get('recipecard').value),
      pickupdeliverytimeframe: new FormControl(this.deliveryinfo.controls[1].get('deliverytimeframe').value?.id),
      shippingmethod: new FormControl(this.order.get('shippingmethod').value, Validators.required),
      deliverydate: new FormControl(this.deliveryinfo.controls[0].get('startdate').value ? this.deliveryinfo.controls[0].get('startdate').value : dateNextWeek),
      shippingaddresscountrycode: new FormControl(this.steps.controls[0].get('order').get([1]).get('countrycode').value, Validators.required),
      couponcode: new FormControl(this.steps.controls[2].get('couponcode').value === '' ? null : this.steps.controls[2].get('couponcode').value),
      couponcodedidnotreachmov: new FormControl(false),
      orderlines: this.fb.array([])
    });

    const lines = order.get('orderlines') as FormArray;

    this.orderlines.controls.forEach(val => {
      lines.push(val);
    });
    return order;
  }

  //Telemarketing
  private get TMsteps(): FormArray {
    return this.telemarketingForm.controls.steps as FormArray;
  }
  get TMorder(): FormGroup {
    return this.TMsteps.controls[0] as FormGroup;
  }
  get TMorderlines(): FormArray {
    return this.TMorder.get('orderlines') as FormArray;
  }
  get TMorderrequest(): FormGroup {

    //const dateToday = formatDate(new Date(), 'yyyy-MM-dd', ɵDEFAULT_LOCALE_ID);
    const dateNextWeek = formatDate(new Date(new Date().getTime() + 8 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd', ɵDEFAULT_LOCALE_ID);

    const order = new FormGroup({
      customerid: new FormControl(this.TMsteps.controls[1].get('customer').get('id').value),
      email: new FormControl(this.TMsteps.controls[1].get('customer').get('email').value),
      brandid: new FormControl(environment.brandid, [Validators.required, Validators.min(1)]),
      originentitytype: new FormControl(null),
      recipecard: new FormControl(this.TMorder.get('recipecard').value),
      pickupdeliverytimeframe: new FormControl(this.TMsteps.controls[3].get('deliverytimeframe').value?.id),
      shippingmethod: new FormControl(this.TMorder.get('shippingmethod').value, Validators.required),
      deliverydate: new FormControl(this.TMsteps.controls[3].get('startdate').value ? this.TMsteps.controls[3].get('startdate').value : dateNextWeek),
      shippingaddresscountrycode: new FormControl(this.TMsteps.controls[2].get('address').get('countrycode').value, Validators.required),
      couponcode: new FormControl(this.TMsteps.controls[4].get('couponcode').value === '' ? null : this.TMsteps.controls[4].get('couponcode').value),
      couponcodedidnotreachmov: new FormControl(false),
      orderlines: this.fb.array([])
    });

    const lines = order.get('orderlines') as FormArray;

    this.TMorderlines.controls.forEach(val => {
      lines.push(val);
    });
    return order;
  }

  getFlowOrderlines(flowtype: Flowtype): FormArray {
    switch (flowtype) {
      case Flowtype.SINGLEORDER:
        return this.orderlines;
      case Flowtype.TELEMARKETING:
        return this.TMorderlines;
      case Flowtype.SUBSCRIPTION:
        return this.subscriptionOrderflowLines;
    }
  }
  getFlowOrder(flowtype: Flowtype): FormGroup {
    switch (flowtype) {
      case Flowtype.SINGLEORDER:
        return this.order;
      case Flowtype.TELEMARKETING:
        return this.TMorder;
      case Flowtype.SUBSCRIPTION:
        return this.orderFlowForm;
    }
  }

  setOrderFlowFormAddresses() {
    this.orderFlowForm.get('shippingtoname').setValue(this._customerService.getCustomerName);
    this.orderFlowForm.get('billingtoname').setValue(this._customerService.getCustomerName);

    const customerOrderShippingAddress = this._customerService.getCustomerAddressByAddressId(this._addressService.shippingAddressForm.get('id').value)
    if (!customerOrderShippingAddress) return;
    this.orderFlowForm.get('shippingaddress').setValue(customerOrderShippingAddress.id);
    this.orderFlowForm.get('billingaddress').setValue(customerOrderShippingAddress.id);
  }

  private clearCustomerOrderdata() {
    this._orders = [];
    this.shownPage = 0;
    this.OrderlistSubject.next(null)
    if (isPlatformBrowser(this.platformId))
      this.window.clearTimeout(this.orderlistRefreshTimer);
  }

  clearOrderFlowForm(flowType: Flowtype) {

    this.orderFlowForm.get('deliverydate').reset(this.assumedDeliverydate);
    this.orderFlowForm.get('pickupdeliverytimeframe').reset(null);
    this.orderFlowForm.get('pickupstore').reset(null);
    this.orderFlowForm.get('storeid').reset(null);
    this.orderFlowForm.get('storetype').reset(null);
    this.orderFlowForm.get('couponcode').reset(null);
    this.orderFlowForm.get('couponcodedidnotreachmov').reset(false);
    this.orderFlowForm.get('deliveryinstructions').reset('');

    if (flowType === Flowtype.SUBSCRIPTION) {
      this.orderFlowForm.get('originentityid').reset(null);
      this.orderFlowForm.get('originentitytype').reset(null);
      this.orderFlowForm.get('firstsubscriptionorder').reset(false);
      this.orderFlowForm.get('recipecard').reset(false);

      this.subscriptionOrderflowLines.clear()
    }

  }

  private saveNewOrderInSession() {
    const value = this.singleOrderForm.getRawValue();
    if (value.steps[1].user) { //possible blocked by cookiebot
      value.steps[1].user.password = null;
      value.steps[1].user.confirmPassword = null;
    }
    this._sessionStorage.setObject('neworder', value);
  }
  clearNewOrderForms() {

    this._sessionStorage.removeItem('neworder');
    this._sessionStorage.removeItem('newordersteps');
    this._sessionStorage.removeItem('orderflowform');

    this.assumedDeliverydate = formatDate(this._globalService.addDays(new Date(), 8), 'yyyy-MM-dd', ɵDEFAULT_LOCALE_ID);

    this.initOrderflowForm();
    const emptySingleOrderform = this.initSingleOrderform().getRawValue();
    if (emptySingleOrderform.steps[1].customer['_loginfailed'] === undefined)
      emptySingleOrderform.steps[1].customer['_loginfailed'] = false;
    this.singleOrderForm.patchValue(this.initSingleOrderform().getRawValue());
    //recreates customerform
    this.customer.get('donotcontact').setValue(true);
    this.customerAddresses.clear();
    //this.customerOptins.clear();
    if (this.userRoles) {
      this.userRoles.clear();
      this.userRoles.push(new FormGroup({ id: new FormControl(null), role: new FormControl(Role.CUSTOMER) }))
    }
    this.orderlines.clear();
    this.subscriptionOrderflowLines.clear();

    if (this.singleOrderForm.get('steps').get([1]).get('user'))
      this.singleOrderForm.get('steps').get([1]).get('user').enable();

    this.orderFlowForm.get('deliverydate').setValue(this.assumedDeliverydate, { emitEvent: true });
    // this.recipeAvailabilityPeriod = null;
    this.recipeAvailabilityPeriod$.next(null);

    this.setNewOrderCouponcode(this._couponcodeService.couponcode$.value);
  }
  private setNewOrderCouponcode(couponcodedata: CouponcodeCode) {
    // const couponcodedata = this._localStorage.getObject(('couponcode')) as CouponcodeCode;
    if (!couponcodedata || couponcodedata && !couponcodedata.isusable) {
      this.steps.controls[2].get('couponcode').setValue(null);
      this.orderFlowForm.get('couponcode').setValue(null);
      return;
    }

    if (couponcodedata.personalemail && couponcodedata.personalemail !== '') {

      // always first set email to be able to check the couponcode
      this.customer.get('email').setValue(couponcodedata.personalemail); //this.customer is customer from singleorderflow
      this.customer.get('email').disable();
      this._customerService.customerFlowform.get('email').setValue(couponcodedata.personalemail);
      this._customerService.customerFlowform.get('email').disable();
    }
    this.steps.controls[2].get('couponcode').setValue(couponcodedata.code);
    this.orderFlowForm.get('couponcode').setValue(couponcodedata.code, { emitEvent: true })
  }

  private initOrderflowForm() {
    this._refreshOrderflowForm$.next();

    if (this.orderFlowForm)
      this.orderFlowForm.reset({}, { emitEvent: false });

    this.orderFlowForm = this.initCustomercreateorderRequest();

    this.orderFlowForm.valueChanges.pipe(
      takeUntil(this._refreshOrderflowForm$)
    ).subscribe({
      next: res => {
        this._sessionStorage.setObject('orderflowform', res)
      }
    })

    const SessionorderFlowForm = this._sessionStorage.getObject('orderflowform');
    if (SessionorderFlowForm) {
      this.orderFlowForm.patchValue(SessionorderFlowForm);
    }
    const currentDeliverydate = this.orderFlowForm.get('deliverydate').value;
    if (!currentDeliverydate || currentDeliverydate === '' || currentDeliverydate === null || currentDeliverydate === undefined) {
      this.orderFlowForm.get('deliverydate').setValue(this.assumedDeliverydate, { emitEvent: false });
    }

    this.orderFlowForm.get('recipecard').valueChanges.pipe(
      takeUntil(this._refreshOrderflowForm$)
    ).subscribe(() => {
      this.setCalculatedPrices(Flowtype.SUBSCRIPTION)
    })
    this.orderFlowForm.get('couponcode').valueChanges.pipe(
      takeUntil(this._refreshOrderflowForm$)
    ).subscribe(() => {
      this.setCalculatedPrices(Flowtype.SUBSCRIPTION)
    })
  }
  private initSingleOrderform(): FormGroup {
    let fg = new FormGroup({
      steps: this.fb.array([

        // STEP-0
        new FormGroup({
          order: this.fb.array([
            // ORDER-0
            new FormGroup({
              calculatedPrice: new FormControl(null),
              shipping: new FormControl(0),
              shippingmethod: new FormControl(Shippingmethod.DOOR_DELIVERY),
              deliverydate: new FormControl(''),
              recipecard: new FormControl(false),
              orderlines: this.fb.array([], Validators.required),
              shippingaddress: new FormControl(null)
            }),
            //ORDER-1
            new FormGroup({
              postalcode: new FormControl('', [Validators.required, Validators.pattern(new RegExp(/^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-z]{2}$/i))]),
              countrycode: new FormControl(null, [Validators.required]),
              isExistingUser: new FormControl(false), //needed for displaying usercreationpage or not
              // donotcontact: new FormControl(true),
            }),
            //ORDER-2
            new FormGroup({
              deliveryinfo: this.fb.array([
                //deliveryinfo-0
                new FormGroup({
                  startdate: new FormControl('', Validators.required),
                  storepickup: new FormControl(false),

                  subscriptionid: new FormControl(null),

                }),
                //deliveryinfo-1
                new FormGroup({
                  store: new FormControl(''),
                  deliverytimeframe: new FormControl('', Validators.required),
                }),
                //deliveryinfo-2
                new FormGroup({
                  storedata: new FormControl(''),
                  deliveryinstructions: new FormControl('', Validators.maxLength(MAX_DELIVERY_INSTRUCTIONS_LENGTH)),
                }),
              ]),
            })
          ])
        }),

        // STEP-1
        new FormGroup({
          customer: this._customerService.customerForm,
          address: this._addressService.shippingAddressForm,
          user: this._customerService.userForm
        }),

        // STEP-2
        new FormGroup({
          couponcode: new FormControl(null)
        }),
        // STEP-3
        new FormGroup({
          paymentmethod: new FormControl(null, Validators.required),
          creditcardnetwork: new FormControl(null, Validators.required)
        })
      ])
    });

    let order = fg.get('steps').get('0').get('order');
    let countrycode = fg.get('steps').get('0').get('order').get('1').get('countrycode')
    let postalcode = fg.get('steps').get('0').get('order').get('1').get('postalcode')

    countrycode.valueChanges.subscribe(res => {
      if (res === Countrycode.NL) {
        postalcode.setValidators([Validators.required, Validators.pattern(new RegExp(/^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-z]{2}$/i))]);
      }

      if (res === Countrycode.BE) {
        postalcode.setValidators([Validators.required, Validators.pattern(new RegExp(/^[1-9][0-9]{3}$/i))]);
      }
      postalcode.updateValueAndValidity();
      order.updateValueAndValidity();
    })

    return fg;
  }

  private initCustomercreateorderRequest(): FormGroup {
    const fg = new FormGroup({
      customerid: this._customerService.customerFlowform.get('id'),
      email: new FormControl(this._customerService.customerFlowform.getRawValue().email), // this._customerService.customerFlowform.get('email'),
      brandid: new FormControl(environment.brandid, [Validators.required, Validators.min(1)]),
      currency: new FormControl(Currency.EUR),
      originentityid: new FormControl(null),
      originentitytype: new FormControl(null),

      shippingmethod: new FormControl(Shippingmethod.DOOR_DELIVERY, Validators.required),
      deliverydate: new FormControl(null, Validators.required), //later to subscriptionform -> firstdeliverydate?
      pickupdeliverytimeframe: new FormControl(null, Validators.required), //later to subscriptionform?
      pickupstore: new FormControl(null), //later to subscriptionform?
      firstsubscriptionorder: new FormControl(false),
      storeid: new FormControl(null),//later to subscriptionform?
      storetype: new FormControl(null), //later to subscriptionform?
      shippingtoname: new FormControl(this._customerService.getCustomerName),
      billingtoname: new FormControl(this._customerService.getCustomerName),
      shippingaddress: new FormControl(this._customerService.getCustomerShippingAddressId), //
      billingaddress: new FormControl(this._customerService.getCustomerBillingAddressId), //
      shippingaddresscountrycode: this._addressService.shippingAddressForm.get('countrycode'),

      calculatedPrice: new FormControl(null),
      couponcode: new FormControl(null),  //later to subscriptionform?
      couponcodedidnotreachmov: new FormControl(false),
      recipecard: new FormControl(true),  //later to subscriptionform?
      paymentmethod: new FormControl(null, Validators.required),
      creditcardnetwork: new FormControl(null),
      deliveryinstructions: new FormControl('', Validators.maxLength(500)),
      orderlines: this.fb.array([])
    });

    this._customerService.customerFlowform.valueChanges.pipe(
      filter(res => res.email)
    ).subscribe(res => {
      fg.get('email').setValue(res.email);
    })
    this._customerService.customerForm.statusChanges.pipe(
      filter(() => this._customerService.customerForm.getRawValue().email)
    ).subscribe(res => {
      fg.get('email').setValue(this._customerService.customerForm.getRawValue().email);
    })

    fg.get('paymentmethod').valueChanges.pipe().subscribe(value => {
      if (value === Paymentmethod.CREDITCARD) {
        fg.get('creditcardnetwork').setValidators(Validators.required);
      } else {
        fg.get('creditcardnetwork').clearValidators();
      }
      // fg.get('bic').updateValueAndValidity();
      fg.get('creditcardnetwork').updateValueAndValidity();
    });

    return fg;
  }

  addOrderline(orderline: CustomerCreateOrderLine, emitevent = true, flowtype = Flowtype.SINGLEORDER): boolean {

    orderline.sku = (orderline.sku === "" || orderline.sku === undefined) ? null : orderline.sku;

    const orderlines = this.getFlowOrderlines(flowtype);
    const existingOrderline = orderlines.controls.filter(c => {
      //this is for recipecard lines
      if (!orderline.sku && !orderline.boxid && !orderline.recipeid)
        return;

      if (orderline.sku && orderline.sku !== c.value.sku)
        return;
      if (orderline.boxid && orderline.boxid !== c.value.boxid)
        return;
      if (orderline.recipeid && orderline.recipeid !== c.value.recipeid)
        return;

      if (c.value.persons !== orderline.persons)
        return;
      if (c.value.couponcodeitem !== orderline.couponcodeitem)
        return;
      return orderline;
    })[0]


    //checks if there arent already 9 recipes added EK-1331 
    //changed for EK-1459, to make it more general and also let it work for singleorders
    if (this.hasReachedMaxRecipecount([...orderlines.value, ...[orderline]]))
      return false;

    //had to update the qty in this workaround because it otherwise didnt trigger the valuechanges on de whole form to save it to the sessionstorage
    if (existingOrderline) {

      const orderlineIndex = orderlines.controls.indexOf(existingOrderline)
      existingOrderline.get('quantity').patchValue(existingOrderline.get('quantity').value + orderline.quantity);
      orderlines.removeAt(orderlineIndex);
      if (orderlines.controls.length == 0) {
        orderlines.push(existingOrderline);
      } else {
        orderlines.insert(orderlineIndex, existingOrderline);
      }
      this.setCalculatedPrices(flowtype);
      this.setSingleorderRecipesCount();
      return true;
    }

    const g = new FormGroup({
      sku: new FormControl(null),
      recipeid: new FormControl(null),
      boxid: new FormControl(null),
      persons: new FormControl(null),
      description: new FormControl(''),
      quantity: new FormControl(null, Validators.required),
      webshopitem: new FormControl(false),
      onetimebox: new FormControl(false),
      issubscriptionitem: new FormControl(false),
      calculatedPrice: new FormControl(null),
      couponcodeitem: new FormControl(false),
      doublepersons: new FormControl(false),
      object: new FormControl(null)
    });
    g.patchValue(orderline);

    if (g.get('boxid').value !== null || g.get('recipeid').value !== null) {
      const max = Math.max(...environment.subscriptionPersonChoices);
      const min = Math.min(...environment.subscriptionPersonChoices)
      g.get('persons').setValidators([Validators.min(min), Validators.max(max)]);
      g.updateValueAndValidity();
    }
    g.get('persons').valueChanges.subscribe(() => {
      this.setCalculatedPrices(flowtype);
      this.saveNewOrderInSession();
    });
    g.get('quantity').valueChanges.subscribe(() => {
      this.setCalculatedPrices(flowtype);
      this.saveNewOrderInSession();

    });

    this.setOrderline(g);

    orderlines.push(g, { emitEvent: emitevent });
    if (!orderline.couponcodeitem && (orderline.sku || orderline.boxid || orderline.recipeid))
      this.setCalculatedPrices(flowtype);
    this.setSingleorderRecipesCount();
    return true


  }
  createNewOrderline(product: Product, box: Box, recipe: Recipe, persons: number = null, quantity: number = 1, couponcodeitem: boolean = false, subscriptionitem: boolean = false, webshopitem: boolean = false, onetimebox: boolean = false, flowtype = Flowtype.SINGLEORDER): boolean {

    if (box && this._customerService.getCustomerLifestyles.length == 0 && box.associatedlifestyle) {
      this._customerService.addLifeStyle(new CustomerLifestyle(null, box.associatedlifestyle))
    }

    const line: CustomerCreateOrderLine = {
      sku: product?.sku,
      recipeid: recipe?.id,
      boxid: box?.id,
      persons: persons,
      description: product?.title || box?.title || recipe?.displayname,
      quantity: quantity,
      issubscriptionitem: subscriptionitem, //has to be true to be able to have different prices for different quantities of persons, it should be made dynamic when there also should be able to add webshop products to an singleorder
      webshopitem: webshopitem,
      onetimebox: onetimebox,
      calculatedPrice: null,
      doublepersons: recipe ? recipe?.doublepersons : box ? box?.doublepersons : false,
      couponcodeitem: couponcodeitem,
    }

    line['object'] = product || box || recipe;

    return this.addOrderline(line, true, flowtype);
    //this.setCalculatedPrices(flowtype);
  }
  removeOrderline(line: FormGroup, flowtype = Flowtype.SINGLEORDER) {

    const l = line.value;
    let index = this.getOrderlineIndex(l.sku ? l.object : null, l.boxid ? l.object : null, l.recipeid ? l.object : null, l.couponcodeitem, flowtype);

    this.removeOrderlineByIndex(index, flowtype)

  }
  removeOrderlineByIndex(index: number, flowtype = Flowtype.SINGLEORDER) {

    let orderlines = this.getFlowOrderlines(flowtype);

    orderlines.removeAt(index);
    orderlines.markAsDirty();
    this.setCalculatedPrices(flowtype);
    this.setSingleorderRecipesCount();
  }
  private async setOrderline(orderline: FormGroup) {
    // only sets recipeid because that is the only thing available
    if (orderline.get('recipeid').value > 0 && orderline.get('object').value == null) {
      orderline.get('object').setValue(await this._recipeService.getRecipe(orderline.get('recipeid').value).toPromise());
    } else if (orderline.get('boxid').value > 0 && orderline.get('object').value == null) {
      orderline.get('object').setValue(await this._boxService.getBox(orderline.get('boxid').value).toPromise());
    } else if (orderline.get('sku').value !== '' && orderline.get('object').value == null) {
      orderline.get('object').setValue(await this._productService.getProductBySKU(orderline.get('sku').value).toPromise());
    }
    this.setSingleorderRecipesCount();
  }
  getOrderlineIndex(product: Product, box: Box, recipe: Recipe, couponcodeitem: boolean = false, flowtype: Flowtype = Flowtype.SINGLEORDER): number {
    if (!product && !box && !recipe) {
      console.warn('No product, box or recipe given')
      return null;
    }

    let index = this.getOrderlineIndexByElementId(product?.sku, box?.id, recipe?.id, couponcodeitem, flowtype);

    if (index == null || index < 0) {
      console.warn('product, box or recipe not found: ', product?.sku, box?.id, recipe?.id);
      return;
    }

    return index;
  }
  getOrderlineIndexByElementId(sku: string, boxid: number, recipeid: number, couponcodeitem: boolean = false, flowtype: Flowtype = Flowtype.SINGLEORDER): number {
    if (!sku && !boxid && !recipeid) {
      console.warn('No sku, boxid or recipeid given')
      return null;
    }

    const lines = this.getFlowOrderlines(flowtype);
    let index = null;
    if (sku)
      index = lines.value.findIndex(i => i.sku === sku && i.couponcodeitem == couponcodeitem);
    if (boxid)
      index = lines.value.findIndex(i => i.boxid === boxid && i.couponcodeitem == couponcodeitem);
    if (recipeid)
      index = lines.value.findIndex(i => i.recipeid === recipeid && i.couponcodeitem == couponcodeitem);

    if (index == null || index < 0) {
      console.warn('product, box or recipe not found: ', sku, boxid, recipeid);
      return;
    }

    return index;
  }

  getOrderlineByIndex(index: number, flowtype: Flowtype = Flowtype.SINGLEORDER): FormGroup {
    const lines = this.getFlowOrderlines(flowtype);
    return lines.at(index) as FormGroup
  }
  getOrderline(product: Product, box: Box, recipe: Recipe, couponcodeitem: boolean = false, flowtype: Flowtype = Flowtype.SINGLEORDER): FormGroup {
    if (!product && !box && !recipe) {
      console.warn('No product, box or recipe given')
      return null;
    }

    const index = this.getOrderlineIndex(product, box, recipe, couponcodeitem, flowtype);

    return this.getOrderlineByIndex(index, flowtype)

  }
  changeOrderlineByIndex(index: number, flowtype: Flowtype = Flowtype.SINGLEORDER, personsvalue: number = null, quantityvalue: number = null) {
    const line = this.getOrderlineByIndex(index, flowtype)
    if (!line) return;

    this.changeOrderline(line, personsvalue, quantityvalue)

  }
  changeOrderlineByContent(product: Product, box: Box, recipe: Recipe, flowtype: Flowtype = Flowtype.SINGLEORDER, personsvalue: number = null, quantityvalue: number = null, couponcodeitem: boolean = false) {

    const line = this.getOrderline(product, box, recipe, couponcodeitem, flowtype)
    if (!line) return;

    this.changeOrderline(line, personsvalue, quantityvalue)

  }
  changeOrderline(line: FormGroup, personsvalue: number = null, quantityvalue: number = null) {
    if (personsvalue) {
      let l = line.get('persons').value
      if (line.get('boxid').value && line.get('object').value) {
        let box = line.get('object').value as Box;
        let personsindex = box._boxpersons.indexOf(personsvalue);
        //if (personsindex + personschange <= box._boxpersons.length - 1 && personsindex + personschange >= 0)
        if (personsindex >= 0)
          line.get('persons').setValue(personsvalue)
      } else {
        if (personsvalue >= Math.min(...environment.subscriptionPersonChoices) && personsvalue <= Math.max(...environment.subscriptionPersonChoices)) {
          line.get('persons').setValue(personsvalue);
        } else if (l < Math.min(...environment.subscriptionPersonChoices) && l > Math.max(...environment.subscriptionPersonChoices)) {
          line.get('persons').setValue(Math.min(...environment.subscriptionPersonChoices));
        }
      }
    }
    const min = 1;
    if (quantityvalue) {
      const currentQuantity = line.get('quantity').value;
      const updatedQuantity = quantityvalue < min ? min : quantityvalue;
      if (updatedQuantity < currentQuantity) {
        line.get('quantity').setValue(updatedQuantity);
        return true
      }

      line.get('quantity').setValue(updatedQuantity, { emitEvent: false });

      // const orderlinesValue = line.parent.value;
      // const lineindex = line.parent.value.indexOf(line);
      // orderlinesValue.at(lineindex).quantity = updatedQuantity

      if (this.hasReachedMaxRecipecount(line.parent.value)) {
        line.get('quantity').setValue(currentQuantity);
        return false;
      }


      line.get('quantity').setValue(updatedQuantity);

    }
    this.setSingleorderRecipesCount();
  }

  private hasReachedMaxRecipecount(orderlines: Orderline[] | CustomerCreateOrderLine[]): boolean {
    const recipecount = this.getRecipecount(orderlines as unknown as Orderline[])
    if (recipecount > environment.maxRecipeQuantity) {
      this._toastService.openWithTranslate('validation.max_number_of_dishes', 'common.ok_dismiss', Snackbarerrorformat)
      return true;
    }
    return false;
  }

  private getRecipecount(orderlines: Orderline[]): number {
    let recipecount = 0
    orderlines.forEach(o => {
      if (o.recipeid) recipecount = recipecount + o.quantity;
      if (o.boxid) {
        //also checks if there are recipes in a box period
        if (o['object'] && o['object'].boxPeriods && o['object'].boxPeriods.length > 0) {
          const periods = o['object'].boxPeriods as BoxPeriod[];
          recipecount = recipecount + (o.quantity * periods.map(p => p.boxRecipes.length)[0]);
        }
      }
    });
    return recipecount
  }

  private setSingleorderRecipesCount() {
    //needs timeout on lowering quantity
    setTimeout(() => {

      const orderlines = this.orderlines.value as Orderline[];
      const recipecount = this.getRecipecount(orderlines)
      this.singleorderRecipeCount.next(recipecount);

      if (recipecount === 0) {
        if (this.deliveryinfo.get([0]).get('storepickup').value === true)
          this.deliveryinfo.get([0]).get('storepickup').setValue(false);
        if (this.order.get('recipecard').value)
          this.order.get('recipecard').setValue(false);
      } else { //sets recipecards default to true
        if (!this.order.get('recipecard').value)
          this.order.get('recipecard').setValue(true);
      }
    }, 100)
  }
  setCalculatedPrices(flowtype = Flowtype.SINGLEORDER) {

    //To let the couponcode be applied, minimum of a empty string is required as email address

    let request;
    let order;
    let orderlines;
    let emitOrderEvent = false;
    switch (flowtype) {
      case Flowtype.SINGLEORDER:
        request = this.orderrequest.getRawValue();
        order = this.order;
        orderlines = this.orderlines;
        break;
      case Flowtype.TELEMARKETING:
        request = this.TMorderrequest.getRawValue();
        order = this.TMorder;
        orderlines = this.TMorderlines;
        break;
      case Flowtype.SUBSCRIPTION:
        request = this.orderFlowForm.getRawValue();
        request.orderlines = this.subscriptionOrderflowLines.getRawValue();
        request.originentitytype = Originentitytype.RECIPE_SUBSCRIPTION;
        request.firstsubscriptionorder = true;
        order = this.orderFlowForm;
        orderlines = this.subscriptionOrderflowLines
        emitOrderEvent = true;
    }

    if (request) {
      request.orderlines = request.orderlines.filter(l => !l.couponcodeitem)
      request.brandid = environment.brandid;

      if (flowtype === Flowtype.SUBSCRIPTION) {
        const isRecipefirstFlow = isPlatformBrowser(this.platformId) && this.window.location.pathname.includes('/recipefirst');
        if (isRecipefirstFlow) {
          if (!request.deliverydate || request.deliverydate === '') {
            request.deliverydate = this.orderFlowForm.get('deliverydate').value || this.assumedDeliverydate;
          }
          if (!request.shippingmethod || request.shippingmethod === '') {
            request.shippingmethod = this.orderFlowForm.get('shippingmethod').value || Shippingmethod.DOOR_DELIVERY;
          }
        }
      }

      this._pricingService.calculateOrderprice(request).pipe(
        catchError(error => {
          if (error?.code === 205 || error?.message?.includes('Injector has already been destroyed')) {
            return of(null);
          }
          return of(null);
        })
      ).subscribe(
        result => {
          if (!result) return;

          if (orderlines.controls.filter(l => l.value['couponcodeitem'] || (!l.value.sku && !l.value.boxid && !l.value.recipeid)).length > 0)
            do {
              let index = orderlines.controls.findIndex(l => l.value['couponcodeitem'] || (!l.value.sku && !l.value.boxid && !l.value.recipeid))
              orderlines.removeAt(index, { emitEvent: false });
            } while (orderlines.controls.filter(l => l.value['couponcodeitem'] || (!l.value.sku && !l.value.boxid && !l.value.recipeid)).length > 0)
          order.get('calculatedPrice').setValue(result.calculatedPrice, { emitEvent: emitOrderEvent });
          // updates the available orderlines with the new data
          orderlines.controls.forEach((ctrl: FormGroup) => {
            const val = ctrl.value as Orderline;

            if (result.orderlines) {
              const line = result.orderlines.filter(o => {
                if (val.boxid) {
                  return val.boxid === o.boxid && val.quantity === o.quantity && val.persons === o.persons && o.couponcodeitem === false;
                }
                if (val.recipeid) {
                  return val.recipeid === o.recipeid && val.quantity === o.quantity && val.persons === o.persons && o.couponcodeitem === false;
                }
                if (val.sku && val.sku !== '') {
                  return val.sku === o.sku && val.quantity === o.quantity && (!val.persons && !o.persons || val.persons === o.persons) && o.couponcodeitem === false;
                }
              })[0];

              if (line) {
                ctrl.patchValue(line, { emitEvent: false });
                ctrl.get('calculatedPrice').setValue(line.calculatedPrice);
                //let l = this.subscriptionOrderflowLines.at(0);
                //l.get('calculatedPrice').setValue(null)
                //l.get('calculatedPrice').setValue(l.get('calculatedPrice').value)
              } else {
              }
            }
          });

          if (result.orderlines) {
            // adds couponcode orderlines
            result.orderlines.filter(o => o.couponcodeitem === true).forEach(val => {
              this.addOrderline(val, false, flowtype);
            });
            // adds recipecard orderlines
            result.orderlines.filter(l => !l.sku && !l.boxid && !l.recipeid).forEach(val => {
              this.addOrderline(val, false, flowtype);
            });
          }

          orderlines.updateValueAndValidity({ emitEvent: false });
        }
      );
    }

  }

  private resetTimer() {
    if (isPlatformBrowser(this.platformId))
      this.window.clearTimeout(this.orderlistRefreshTimer)
    this.orderlistRefreshTimer = setTimeout(() => {
      let orderlength = this._orders.length;
      this._orders = [];
      this.Orderlist.next({ page: 0, pagesize: orderlength });
    }, this.orderlistRefreshTime)
  }
  getOrdersTable(customerId: number, parameters: TableParameters): Observable<TableResponse<Order>> {
    if (!customerId) return of(null)
    //if has orders and page is smaller than already gotton page and quantity of orders is larger than size of page then just return out of memory
    if (this._orders && this._orders.length > 0 && parameters.page < this.shownPage && this._orders.length >= parameters.page + 1 * parameters.size) {
      let tblresponse = new TableResponse(this._orders, false, false, false, parameters.page, this._orders.length, null, this.pagesize, null, this.totalOrders, this.totalPages, null)

      return of(tblresponse);
    }
    const urlparams = parameters.getUrlParameters();
    return this._http.get<TableResponse<Order>>(environment.apiserver + 'orderbff/v1/order/customerid/' + customerId + urlparams.toString()).pipe(
      map((response: TableResponse<Order>) => {
        this.shownPage++;

        this.totalPages = Math.ceil(response.totalElements / this.pagesize);//makes sure it doesnt fail if there is an other pagesize// response.totalPages;
        this.totalOrders = response.totalElements;
        this._orders = [...new Map([...this._orders, ...response.content].map(order => [order['orderid'], order])).values()];

        //this.OrderlistSubject.next(response.content);

        return response
      })
      , catchError(this.handleError)
    );
  }

  canRateOrderFilter(o: Order): boolean {
    // console.log(new Date().getTime() >= new Date(o.deliverydate).getTime(), new Date(o.deliverydate))
    return o.status !== Orderstatus.CANCELED
      && o.status !== Orderstatus.NEW
      && o.status !== Orderstatus.PAYMENTERROR
      && o.status !== Orderstatus.DISPUTED
      && new Date().getTime() >= new Date(o.deliverydate).getTime()
  }
  hasOrderedEntity(entitytype: Entitytype, entityid: number, sku: string = null, activefrom: string = null, activeto: string = null): boolean {
    if (!entitytype || (!entityid && !sku)) return

    //adds deliverydate to orderlines to be used in the boxcheck
    const orders = this._orders.filter(o => this.canRateOrderFilter(o));
    if (orders.length === 0) return;

    const orderlines = [].concat.apply([], orders.map(o => { return o.orderlines.map(l => { l['deliverydate'] = o.deliverydate; return l; }) })) as Orderline[];

    return orderlines.findIndex(o => {
      if (entitytype === Entitytype.RECIPE) {
        if (entityid === 4927) debugger;
        if (o.recipeid == entityid) return true;

        //checks if recipe is in a box which is ordered
        const boxes = this._boxService.isInBox(entitytype, entityid);
        let val = false
        boxes.forEach(b => {
          //loops through the periods and checks if the box is sold combined with the period
          b.boxPeriods.forEach(p => {
            if (this.hasOrderedEntity(Entitytype.BOX, b.id, null, p.activefrom, p.activeto)) {
              val = true;
              return;
            }
          })
        })
        return val;
      }
      //checks the box in the period if a period is given
      if (entitytype === Entitytype.BOX && activefrom && activeto) {
        if (o.boxid == entityid) {
          //converts the dates to be compared
          let deliverydate = new Date(o['deliverydate']);
          let fromdate = new Date(activefrom);
          let todate = new Date(activeto);
          if (deliverydate.getTime() >= fromdate.getTime() && deliverydate.getTime() <= todate.getTime())
            return true;
        }
        return false;
      }
      if (entitytype === Entitytype.BOX) { return o.boxid == entityid }
      //when product reviews are added, find a solution for products in a ordered box
      if (entitytype === Entitytype.PRODUCT && sku) { return o.sku == sku }
    }) >= 0;

  }

  getOrder(id: number): Observable<Order> {
    return this._http.get<Order>(environment.apiserver + 'orderbff/v1/order/' + id).pipe(
      map((response: Order) => <any>response)
      , catchError(this.handleError)
    );
  }
  getLastOrder(): Observable<Order> {
    this.resetTimer()
    return this._http.get<Order>(environment.apiserver + 'orderbff/v1/order/customer/last').pipe(
      map((response: Order) => <any>response)
      , catchError(this.handleError)
    );
  }
  async getOrderWithTaxDetails(val: Order): Promise<OrderWithTaxDetails> {
    if (!val) return;

    //value is order without added delivery as orderline for displaying
    var value = val as OrderWithTaxDetails
    var subscriptionpricing = await firstValueFrom(this._pricingService.getRecipesubscriptionPricing(value.shippingcountrycode))

    //calculates tax totals
    let taxData: any[] = [];
    let orderForTax = this._globalService.makeUnique(value)

    //tries to get lowest tax percentage for when the shippingcostsvat is not calculated
    let vatLow = subscriptionpricing.vatlow / 100;
    let vatHigh = subscriptionpricing.vathigh / 100;

    //calculates tax percentage, rounds to 1 decimal, to overcome .20 / .21 difference issue
    orderForTax.orderlines.forEach(line => {
      line._taxpercentage = line.vat > 0 ? Math.round((line.vat / (line.price - line.vat)) * 10) : 0
    })

    //TAX PERCENTAGE SHOULD BE FIX TO OLDER ORDERS WHEN TAX PERCENTAGE CHANGES
    //adds shippingcosts for tax overview -> shippingcostvat is undefined when compensationtotal is 100% of the total value
    if (value.shipping) {
      if (!value.shippingcostvat)
        value.shippingcostvat = Math.round(value.shipping * (vatLow / (1 + vatLow)) * 100) / 100
      orderForTax.orderlines.push({ price: orderForTax.shipping, vat: orderForTax.shippingcostvat || 0, _taxpercentage: Math.round(vatLow * 10) } as Orderline)
    }

    let linetotalsInclShipping = orderForTax.orderlines.map(l => l.price).reduce((acc, curr) => acc + curr, 0)
    let orderlinediscount = value.orderlines.map(l => l.discount).reduce((acc, curr) => acc + curr, 0)
    let orderdiscount = (value.discount - orderlinediscount);
    
    //TAX PERCENTAGE SHOULD BE FIX TO OLDER ORDERS WHEN TAX PERCENTAGE CHANGES
    //adds recipecard orderline if it has recipecards and the price difference is 1 euro
    if (!!value.recipecard && Math.round(((linetotalsInclShipping - value.compensationtotal - orderdiscount) + environment.recipecardPrice) * 100) / 100 === value.totalvalue) {

      let vat = Math.round(((environment.recipecardPrice / (1 + vatHigh)) * vatHigh)*100)/100

      this._translateService.get('subscription.recipecard_printed').subscribe(res => {
        let orderline = { description: res, price: environment.recipecardPrice, discount: 0, vat: vat, quantity: 1, _taxpercentage: Math.round(vatHigh * 10) } as Orderline
        value.orderlines.push(orderline)
        orderForTax.orderlines.push(orderline)
      })
    }

    let orderlinetaxtotals = orderForTax.orderlines.map(l => l.vat).reduce((acc, curr) => acc + curr, 0)

    new Set(...[orderForTax.orderlines.map(l => l._taxpercentage)]).forEach((t: number) => {
      if (t > 0)
        taxData.push({ percentage: t, total: 0 })
    })

    taxData.sort((a, b) => a.percentage - b.percentage)

    taxData.forEach(t => {
      let total = 0;
      total = orderForTax.orderlines.filter(l => l._taxpercentage === t.percentage).map(l => l.vat).reduce((accumulator, currentValue) => accumulator + currentValue, 0)
      t.total = total * (orderForTax.vat / orderlinetaxtotals) //recalculation to compensate for average tax rounding in backend
    })

    value._taxdetails = taxData
    return value
  }
  getOrderstatusses(): string[] {
    return Object.keys(Orderstatus);
  }
  getPaymentmethods(): string[] {
    return Object.keys(Paymentmethod);
  }
  getCurrencies(): string[] {
    return Object.keys(Currency);
  }

  private handleError(error: Response) {
    if (error.status === 400) {
      return throwError(() => 'Bad request');
    }
    return throwError(() => error || 'Server error');
  }


}
